// InProcessPopup.js
import React, {useEffect, useState} from 'react';
import {Avatar, AvatarGroup, CircularProgress} from '@mui/material';
import './InProcessPopup.css';
import TripPreview from './TripPreview';
import CallIcon from '@mui/icons-material/Call';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SuggestSelectionPopup from './SuggestSelectionPopup';
import {collection, doc, getDoc, getDocs, addDoc, deleteDoc, serverTimestamp} from "firebase/firestore";
import {useUser} from "../contexts/UserProvider";
import {db} from "../firebase/firebase";
import {
    getRecommendedTrips,
    loadRecommendationsFromDB,
    saveRecommendationsForTrip
} from "../trip-recommender-server/RecommendationServer";
import ProfileDialog from "./ProfileDialog";

const InProcessPopup = ({isOpen, onClose, profile: profileData, style}) => {
    const [selectedTripId, setSelectedTripId] = useState(null);
    const [showSuggestPopup, setShowSuggestPopup] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [otherProfile, setOtherProfile] = useState(null);
    const {authUser, profile} = useUser();
    const [tripSuggestions, setTripSuggestions] = useState([]);
    const [chosenTemplateId, setChosenTemplateId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleAvatarClick = () => {
        setShowProfileModal(true);
    };

    useEffect(() => {
        (async () => {
            if (!isOpen || !profileData) return;
            const otherUserId = profileData.participants.find(p => p !== authUser.uid);
            const otherUserDoc = await getDoc(doc(db, "users", otherUserId));
            setOtherProfile({id: otherUserId, ...otherUserDoc.data()});
        })();
    }, [isOpen, profileData]);

    useEffect(() => {
        const fetchTemplatesAndSuggestions = async () => {
            if (!isOpen || !profileData) return;

            try {
                // Load all trip templates from Firestore
                setIsLoading(true);
                const templatesSnapshot = await getDocs(collection(db, "tripTemplates"));
                const templates = templatesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // Try to load the save recommendations from firebase
                const savedIds = await loadRecommendationsFromDB(profileData.id);

                let selectedTripTemplates = [];

                if (savedIds && savedIds.length > 0) {
                    // Find only the templates whose ID is in the list of recommendations
                    selectedTripTemplates = templates.filter(t => savedIds.includes(t.id));
                    console.log("המלצות נטענו מ־DB:", savedIds);
                } else {
                    // If there are no saved recommendations – call Gemini
                    const rawSuggestions = await getRecommendedTrips(profileData, templates);
                    if (!rawSuggestions) return;

                    const parsedSuggestions = parseGeminiSuggestions(rawSuggestions);

                    selectedTripTemplates = templates.filter(template =>
                        parsedSuggestions.some(s => s.id === template.id || s.title === template.title)
                    );

                    const recommendedIds = selectedTripTemplates.map(t => t.id);
                    await saveRecommendationsForTrip(profileData.id, recommendedIds);
                    console.log("נשמרו המלצות חדשות:", recommendedIds);
                }
                setIsLoading(false);
                setTripSuggestions(selectedTripTemplates);
            } catch (error) {
                console.error("שגיאה בהבאת תבניות או המלצות:", error);
            }
        };

        fetchTemplatesAndSuggestions();
    }, [isOpen, profileData]);

    const parseGeminiSuggestions = (text) => {
        const lines = text.split('\n').filter(line => line.trim());
        return lines
            .filter(line => line.startsWith('-'))
            .map(line => {
                const cleanedLine = line.replace(/^-\s*/, '').trim();
                return {id: cleanedLine, title: cleanedLine};
            });
    };

    const sendTripSuggestion = async ({fromUserId, toUserId, tripTemplateId, suggestedDate}) => {
        try {
            await addDoc(collection(db, "tripSuggestionsSent"), {
                fromUserId,
                toUserId,
                tripTemplateId,
                suggestedDate,
                createdAt: serverTimestamp()
            });
            console.log("הצעת טיול נשמרה בהצלחה");
        } catch (error) {
            console.error("שגיאה בשליחת הצעת הטיול:", error);
        }
    };

    // Function for creating a trip in tripEvents
    const handleGroupTripCreation = async ({date, groupSize}) => {
        try {
            // Determining the maximum number of participants by groupSize
            const maxParticipantsMap = {
                pair: 3,
                small: 6,
                large: 50, // A big limit for קבוצה גדולה (7+)
            };

            const maxParticipants = maxParticipantsMap[groupSize] || 50; // default

            const newTrip = {
                createdAt: profileData.createdAt,
                createdBy: profile.id,
                participants: profileData.participants,
                templateId: chosenTemplateId,
                timestamp: new Date(date),
                maxParticipants,
                status: "open"
            };

            await addDoc(collection(db, "tripEvents"), newTrip);
            await deleteDoc(doc(db, "SingleUserTrips", profileData.id));
            console.log("טיול קבוצתי נוצר ונמחק הטיול הקודם");
        } catch (error) {
            console.error("שגיאה ביצירת טיול קבוצתי:", error);
        }
    };


    return (
        !isOpen || !profileData || !otherProfile ? null : (
            <>
                <div className="popup-overlay" onClick={onClose} style={style}>
                    <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                        <div className="trip-process-participants">
                            <AvatarGroup spacing="medium" max={2} sx={{'& .MuiAvatar-root': {width: 64, height: 64}}}>
                                <Avatar alt={profile?.name} src={profile?.image} title="את/ה"/>
                                <Avatar
                                    alt={otherProfile?.title || ""}
                                    src={otherProfile?.image || ""}
                                    title={otherProfile?.title || ""}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAvatarClick();
                                    }}
                                    sx={{cursor: 'pointer'}}
                                />
                            </AvatarGroup>
                        </div>

                        <button className="close-button" onClick={onClose}>✖</button>

                        <h2>פרטי התקשרות</h2>
                        <p><strong>שם:</strong> {otherProfile?.title}</p>
                        <p>
                            <strong>טלפון:</strong> {otherProfile?.phoneNumber}
                            <WhatsAppIcon className="whatsapp-icon"/>
                            <CallIcon className="call-icon"/>
                        </p>

                        {profileData.favoriteTrips?.length > 0 && (
                            <div className="trip-suggestions">
                                <h2>טיולים מועדפים</h2>
                                <div className="trips-list-suggestions">
                                    {profileData.favoriteTrips.map((trip) => (
                                        <TripPreview
                                            key={trip.id}
                                            trip={trip}
                                            showProfile={false}
                                            showMoreInfoLink={true}
                                            hideDate={true}
                                            compactModal={true}
                                            suggestionMode={true}
                                            currentUserId={authUser.uid}
                                            tripCreatorId={profileData.createdBy}
                                            onSelectTrip={(tripId) => {
                                                setSelectedTripId(tripId);
                                                setShowSuggestPopup(true);
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        {
                            isLoading && (
                                <div className="loadings">
                                    <CircularProgress color="success" size="4rem"/>
                                </div>
                            )
                        }
                        {tripSuggestions.length > 0 && !isLoading && (
                            <div className="trip-suggestions">
                                <div className="trip-suggestions-header">
                                    <h2>הצעות לטיולים</h2>
                                    <img src="/images/gemini-icon.svg" alt="Gemini" className="gemini-icon"/>
                                </div>
                                <div className="trips-list-suggestions">
                                    {tripSuggestions.map((trip) => (
                                        <TripPreview
                                            key={trip.id}
                                            trip={trip}
                                            showProfile={false}
                                            showMoreInfoLink={true}
                                            hideDate={true}
                                            compactModal={true}
                                            suggestionMode={true}
                                            currentUserId={authUser.uid}
                                            tripCreatorId={profileData.createdBy}
                                            onSelectTrip={(tripId) => {
                                                setSelectedTripId(tripId);
                                                setChosenTemplateId(tripId);    // The suggestion trip that the user chose
                                                setShowSuggestPopup(true);
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <ProfileDialog
                    profileId={otherProfile.id}
                    open={showProfileModal}
                    onClose={() => setShowProfileModal(false)}
                />


                <SuggestSelectionPopup
                    open={showSuggestPopup}
                    onClose={() => setShowSuggestPopup(false)}
                    onSubmit={(selectionData) => {
                        if (chosenTemplateId && selectionData?.date && selectionData?.groupSize) {
                            handleGroupTripCreation({
                                date: selectionData.date,
                                groupSize: selectionData.groupSize
                            });
                        }

                        setShowSuggestPopup(false);
                        setSelectedTripId(null);
                        setChosenTemplateId(null);

                        onClose();
                    }}
                />
            </>
        )
    );
};

export default InProcessPopup;
